/**
 * MechaGuard - Storage (持久化封装)
 *
 * localStorage 在几种很常见的情况下会直接抛异常：
 *   - 浏览器隐私模式 / 禁用站点数据
 *   - 配额写满（QuotaExceededError）
 *   - 在 Node 里跑（没有 window）
 *
 * 所以这里统一 try/catch，并在不可用时退化成内存存储 ——
 * 游戏照常玩，只是成就与成绩不会落盘。`isPersistent` 可以告诉 UI
 * 当前是不是「临时存档」，以便给出提示。
 */

/** 内存版存储（Node 测试 / 隐私模式兜底） */
class MemoryStorage {
  constructor() {
    /** @type {Map<string,string>} */
    this.map = new Map();
  }

  getItem(key) {
    return this.map.has(key) ? this.map.get(key) : null;
  }

  setItem(key, value) {
    this.map.set(key, String(value));
  }

  removeItem(key) {
    this.map.delete(key);
  }

  clear() {
    this.map.clear();
  }
}

/**
 * 显式创建一个内存存储。
 *
 * 测试专用 —— 冒烟测试会真的去解锁成就、提交排行榜记录，
 * 如果直接用默认存储就会写进玩家的真实存档（localStorage）。
 * 传一个内存实例进去，测试怎么跑都不会污染玩家的数据。
 */
export function createMemoryStorage() {
  return new MemoryStorage();
}

/**
 * 创建可用的存储后端。
 *
 * 只有在**真实浏览器**（存在 window）里才使用 localStorage。
 * 原因：Node 22+ 的实验性 Web Storage API 也会提供全局 localStorage，
 * 但它默认只是内存实现（除非用 --localstorage-file 指定文件）。
 * 那种情况下写读探测会「成功」，于是 isPersistent 被谎报成 true，
 * 调用方会以为成绩已经落盘 —— 所以这里用 window 是否存在来区分
 * 「真浏览器」和「Node 的模拟实现」。
 *
 * 在浏览器里仍会做一次写读探测，确认真的能持久化（隐私模式会抛异常）。
 */
export function createStorage() {
  if (typeof window === 'undefined') {
    return { backend: new MemoryStorage(), isPersistent: false };
  }

  try {
    if (typeof localStorage !== 'undefined' && localStorage) {
      const probe = '__mechguard_probe__';
      localStorage.setItem(probe, '1');
      const ok = localStorage.getItem(probe) === '1';
      localStorage.removeItem(probe);
      if (ok) {
        return { backend: localStorage, isPersistent: true };
      }
    }
  } catch {
    // 隐私模式 / 禁用站点数据 / 配额满：落到内存版
  }

  return { backend: new MemoryStorage(), isPersistent: false };
}

/**
 * 安全读取 JSON。任何异常（键不存在、内容损坏、被外部改坏）都返回 fallback，
 * 不会让游戏因为一条脏存档而起不来。
 * @param {{getItem:Function}} storage
 * @param {string} key
 * @param {*} fallback
 */
export function readJson(storage, key, fallback) {
  try {
    const raw = storage.getItem(key);
    if (raw === null || raw === undefined || raw === '') return fallback;

    const parsed = JSON.parse(raw);
    return (parsed === null || parsed === undefined) ? fallback : parsed;
  } catch {
    return fallback;
  }
}

/**
 * 安全写入 JSON
 * @returns {boolean} 是否写入成功（配额满会返回 false）
 */
export function writeJson(storage, key, value) {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** 安全读取字符串 */
export function readString(storage, key, fallback = '') {
  try {
    const raw = storage.getItem(key);
    return (raw === null || raw === undefined) ? fallback : String(raw);
  } catch {
    return fallback;
  }
}

/** 安全写入字符串 */
export function writeString(storage, key, value) {
  try {
    storage.setItem(key, String(value));
    return true;
  } catch {
    return false;
  }
}
